import React, { createContext, useContext, useState } from 'react';

const STORAGE_KEY = 'aby-cookie-consent';
const VERSION = 1;
const ConsentContext = createContext(null);

function readStored() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (typeof data.analytics !== 'boolean' || typeof data.marketing !== 'boolean') return null;
    return data;
  } catch {
    return null;
  }
}

export function ConsentContextProvider({ children }) {
  const [consent, setConsent] = useState(() => {
    const stored = readStored();
    return stored
      ? { status: 'set', analytics: stored.analytics, marketing: stored.marketing }
      : { status: 'unset', analytics: false, marketing: false };
  });
  const [isOpen, setIsOpen] = useState(false);

  const persist = (analytics, marketing) => {
    const payload = { analytics, marketing, version: VERSION, ts: new Date().toISOString() };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    setConsent({ status: 'set', analytics, marketing });
    setIsOpen(false);
  };

  const value = {
    status: consent.status,
    analytics: consent.analytics,
    marketing: consent.marketing,
    isOpen,
    acceptAll: () => persist(true, true),
    rejectAll: () => persist(false, false),
    save: ({ analytics, marketing }) => persist(!!analytics, !!marketing),
    reopen: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentContextProvider');
  return ctx;
}
